import {computed, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {UI_CONFIG} from '@app/core/tokens/ui-config.token';
import {HelpSection} from '@models/help-content';
import helpPageJson from '../../data/help-content.json' with {type: 'json'};

@Injectable({
  providedIn: 'root',
})
export class HelpService {
  private readonly configData = inject(UI_CONFIG);

  helpSections: WritableSignal<HelpSection[]> = signal(helpPageJson as HelpSection[]);

  visibleHelpSections = computed(() => {
    const config = this.configData();
    const sections = this.helpSections();

    if (!config) {
      return undefined; // Indicate that filtering cannot happen yet
    }

    const filterSections = (currentSections: HelpSection[]): HelpSection[] => {
      return currentSections.flatMap(section => {
        const configSlug = section.slug.replaceAll('-', '_');

        const moduleConfig = config['config_data']?.[configSlug];
        const isHiddenByConfig = moduleConfig?.enabled === false;

        if (isHiddenByConfig) {
          return [];
        }

        let filteredSubsections: HelpSection[] | undefined;
        if (section.subsections) {
          filteredSubsections = filterSections(section.subsections);
        }

        return [
          {
            ...section,
            subsections: filteredSubsections,
          },
        ];
      });
    };

    return filterSections(sections);
  });

  sectionsBySlug = computed<{[key: string]: HelpSection}>(() => {
    const helpSections = this.visibleHelpSections();

    if (!helpSections || helpSections.length === 0) {
      return {};
    }

    const map: {[key: string]: HelpSection} = {};
    const addToMap = (section: HelpSection) => {
      map[section.slug] = section;
      if (section.subsections) {
        section.subsections.forEach(sub => addToMap(sub));
      }
    };
    helpSections.forEach(addToMap);

    return map;
  });
}
